import { Draft } from 'immer';
import {
  type BlockCollection,
  type KsAppClientDataNoMeta,
  type Page,
  type PageHeaderSettings,
  type PageTab,
  type PageType,
  type Role,
  emptyAppClientData,
} from '@knapsack/types';
import { makeShortId, slugify } from '@knapsack/utils';
import { BASE_PATHS } from '@/utils/constants';
import {
  SET_APP_CLIENT_DATA,
  SetAppClientData,
  RESET_APP_CLIENT_DATA,
  ResetAppClientData,
  DELETE_PAGE,
  DeletePageAction,
  AddPage,
  ADD_PAGE,
} from './shared.xstate';
import {
  duplicateNavItem,
  duplicatePage,
  duplicateTab,
  mergeDeepInImmer,
  removeBlock,
} from './utils';

export type CustomPagesState = KsAppClientDataNoMeta['customPagesState'];

export interface pageUpdateTitleAction {
  type: 'pages.updateTitle';
  pageId: string;
  title: string;
}

export type CustomPagesActions =
  | AddPage
  | DeletePageAction
  | SetAppClientData
  | ResetAppClientData
  | pageUpdateTitleAction
  | {
      type: 'pages.updateDescription';
      description: string;
      pageId: string;
    }
  | {
      type: 'pages.duplicate';
      pageId: string;
      newId: string;
      newTitle: string;
    }
  | {
      /**
       * Be sure to send in `pages.convertToPattern.cleanup` with `setTimeout` after this event
       */
      type: 'pages.convertToPattern';
      pageId: string;
    }
  | {
      type: 'pages.convertToPattern.cleanup';
      pageId: string;
    }
  | {
      type: 'pages.tabs.add';
      pageId: string;
      tabId: string;
    }
  | {
      type: 'pages.tabs.delete';
      pageId: string;
      tabId: string;
      /**
       * If true, the tab's contents will be moved to the main content area.
       * If false, the tab's contents will be deleted.
       * If undefined, the tab's contents will be deleted.
       */
      moveContents?: boolean;
    }
  | {
      type: 'pages.tabs.duplicate';
      pageId: string;
      tabId: string;
    }
  | {
      type: 'pages.tabs.rename';
      tabId: string;
      value: string;
    }
  | {
      type: 'pages.tabs.reorder';
      pageId: string;
      tabIds: string[];
    }
  | {
      type: 'pages.tabs.setPrivacy';
      tabId: string;
      minRoleNeeded: Role;
    }
  | {
      type: 'pages.tabs.toggleHidden';
      pageId: string;
      tabId: string;
      hidden: boolean;
    }
  | {
      type: 'pagesOrPatterns.updateAliases';
      aliases: string[];
      id: string;
      idType: 'page' | 'pattern';
    }
  | {
      type: 'pagesOrPatterns.settings.pageHeader.reset';
      idType: 'page' | 'pattern';
      id: string;
    }
  | {
      type: 'pagesOrPatterns.settings.pageHeader.update';
      id: string;
      idType: 'page' | 'pattern';
      /** Changes will be merged into `settings.pageHeader` */
      pageHeader: Partial<PageHeaderSettings>;
    }
  | {
      type: 'pagesOrPatterns.settings.tableOfContents.update';
      id: string;
      idType: PageType;
      tableOfContents: Partial<Page['settings']['tableOfContents']>;
    }
  | {
      type: 'pagesOrPatterns.tabs.setPrivacy';
      id: string;
      idType: PageType;
      tabId: string;
      minRoleNeeded: Role;
    }
  | {
      type: 'pagesOrPatterns.tabs.toggleHidden';
      id: string;
      idType: PageType;
      tabId: string;
      hidden: boolean;
    };

export default function customPagesReducer(
  data: Draft<KsAppClientDataNoMeta>,
  action: CustomPagesActions,
): CustomPagesState {
  // providing some backwards compatibility vars for when this was Redux style: return new state shape for this state property
  // each `case` often used Immer's produce for that one `case`, now Immer is called higher up
  const state = data.customPagesState;
  const draft = data.customPagesState;

  switch (action.type) {
    case SET_APP_CLIENT_DATA: {
      data.customPagesState = action.payload.customPagesState;
      return;
    }
    case RESET_APP_CLIENT_DATA:
      data.customPagesState = emptyAppClientData.customPagesState;
      return;
    case ADD_PAGE: {
      // so these pieces of data don't end up in a custom-pages.yml file
      const { skipNav, parentId, ...otherProps } = action.payload;
      draft.pages[action.payload.id] = {
        ...otherProps,
        blockIds: [],
      };
      return;
    }

    case 'pages.duplicate': {
      const { pageId, newId, newTitle } = action;
      duplicatePage({
        pageId,
        newId,
        newTitle,
        data,
      });
      duplicateNavItem({
        navItemId: pageId,
        newNavItemId: newId,
        newNavItemName: newTitle,
        data,
      });
      break;
    }

    case 'pages.updateTitle': {
      const { pageId, title } = action;
      if (title.length === 0) throw new Error('The new title is empty');
      draft.pages[pageId].title = action.title;
      break;
    }

    case 'pages.updateDescription': {
      draft.pages[action.pageId].description = action.description;
      break;
    }
    case 'pages.convertToPattern': {
      const { pageId } = action;
      const page = data.customPagesState.pages[pageId];
      const { id, title, description, blockIds } = page;
      const { byId } = data.navsState;

      if (!page) throw new Error(`Cannot find page "${pageId}"`);

      data.patternsState.patterns[id] = {
        id,
        title,
        description,
        statuses: {},
        templates: [],
        tabs: [{ type: 'subPage', id: 'overview' }],
        subPages: [
          {
            id: 'overview',
            title: 'Overview',
            blockIds: [...blockIds],
          },
        ],
      };

      const newNavItem = {
        parentId: byId[pageId].parentId,
        id,
        path: `${BASE_PATHS.PATTERN}/${id}`,
        name: title,
      };

      byId[id] = newNavItem;
      // we delete the page after converting in `pages.convertToPattern.cleanup` to pattern so we can still access it via the router
      break;
    }
    case 'pages.convertToPattern.cleanup': {
      const { pageId } = action;
      // we delete the page after converting to pattern so we can still access it via the router
      delete draft.pages[pageId];
      break;
    }

    case 'pages.tabs.add': {
      // To create a new tab for a Page, we need to:
      // 0. Determine if existing blockIds (old way) need to be converted to tabIds (new way)
      // 1. Create a new empty block collection
      // 2. Create a new tab, connect to block collection
      // 3. Connect tab to page

      const { pageId, tabId } = action;

      // Does this page have the old blockIds? If so, we must move those blockIds to a new tab
      const { blockIds = [] } = draft.pages[pageId];

      const blockCollectionId = makeShortId();

      const blockCollection: BlockCollection = {
        id: blockCollectionId,
        blockIds: [],
      };
      const tab: PageTab = {
        id: tabId,
        type: 'block-collection-tab', // Hardcoded for now, expand as we provide more tab types
        title: 'untitled',
        slug: 'untitled',
        blockCollectionIds: [blockCollectionId],
      };

      if (!draft.pages[pageId]) {
        console.error(`Cannot add tab: Page with ID "${pageId}" not found`);
        break;
      }

      // Were there pre-existing blockIds that must now be made members of this new collection?
      if (blockIds.length > 0) {
        blockCollection.blockIds = blockIds;
        // Nuke the old blockIds from the page. Conversion from blockIds to tabIds is complete.
        draft.pages[pageId].blockIds = [];
      }
      // Ensure blockCollections key exists
      data.db.blockCollections ??= { byId: {}, settings: {} };
      data.db.blockCollections.byId ??= {};
      data.db.blockCollections.settings ??= {};
      // Create block collection data
      data.db.blockCollections.byId[blockCollectionId] = blockCollection;
      // Ensure tabs key exists
      data.db.tabs ??= { byId: {}, settings: {} };
      data.db.tabs.byId ??= {};
      data.db.tabs.settings ??= {};
      // Create tab data
      data.db.tabs.byId[tabId] = tab;
      // Ensure page has tabIds key
      draft.pages[pageId].tabIds ??= [];
      // Modify page to include tab
      draft.pages[pageId].tabIds.push(tabId);

      break;
    }
    case 'pages.tabs.delete': {
      const { pageId, tabId, moveContents } = action;

      const page = draft.pages[pageId];
      if (!page) {
        console.error(`Cannot delete tab: Page with ID "${pageId}" not found`);
        break;
      }

      // Is tabId even found in page's tabIds?
      const indexOfTab = page.tabIds.indexOf(tabId);
      if (indexOfTab === -1) {
        console.error(
          `Cannot delete tab: Tab with ID "${tabId}" not found in page with ID "${pageId}"`,
        );
        break;
      }

      // Remove tab from page by mutating draft
      draft.pages[pageId].tabIds.splice(indexOfTab, 1);

      // Does tab even exist in db?
      const tab = data.db.tabs.byId[tabId];
      if (!tab) {
        console.error(
          `Cannot delete tab: Tab with ID "${tabId}" not found in db`,
        );
        break;
      }

      if (!moveContents) {
        // Delete block collections that were in tab, and all block in block collections
        tab.blockCollectionIds.forEach((blockCollectionId) => {
          data.db.blockCollections.byId[blockCollectionId].blockIds.forEach(
            // delete every block in block collection
            (blockId) => {
              removeBlock({
                blockId,
                data,
              });
            },
          );
          // Finaly delete block collection itself
          delete data.db.blockCollections.byId[blockCollectionId];
        });
      } else {
        // Move blocks to main content
        draft.pages[pageId].blockIds.push(
          ...tab.blockCollectionIds.flatMap(
            (blockCollectionId) =>
              data.db.blockCollections.byId[blockCollectionId].blockIds,
          ),
        );
        // Delete block collections that were in tab
        tab.blockCollectionIds.forEach((blockCollectionId) => {
          delete data.db.blockCollections.byId[blockCollectionId];
        });
      }
      delete data.db.tabs.byId[tabId];
      break;
    }
    case 'pages.tabs.duplicate': {
      const { pageId, tabId } = action;
      const tab = data.db.tabs.byId[tabId];
      const page = data.customPagesState.pages[pageId];
      if (!page) {
        console.error(
          `Cannot duplicate tab: Page with ID "${pageId}" not found`,
        );
        break;
      }
      if (!tab) {
        console.error(`Cannot duplicate tab: Tab with ID "${tabId}" not found`);
        break;
      }
      duplicateTab({
        pageId,
        tabId,
        data,
      });
      break;
    }
    case 'pages.tabs.rename': {
      const { tabId, value } = action;
      const tab = data.db.tabs.byId[tabId];
      if (!tab) {
        console.error(`Cannot rename tab: Tab with ID "${tabId}" not found`);
        break;
      }
      tab.slug = slugify(value);
      tab.title = value;
      break;
    }

    case 'pages.tabs.reorder': {
      const { pageId, tabIds } = action;
      const page = draft.pages[pageId];
      if (!page) {
        console.error(
          `Cannot reorder tabs: Page with ID "${pageId}" not found`,
        );
        break;
      }
      page.tabIds = tabIds;
      break;
    }

    case 'pages.tabs.setPrivacy': {
      const { tabId, minRoleNeeded } = action;
      const tab = data.db.tabs.byId[tabId];
      if (!tab) {
        console.error(`Cannot set privacy: Tab with ID "${tabId}" not found`);
        break;
      }
      if (!tab.options) {
        tab.options = {};
      }
      if (!minRoleNeeded) {
        delete tab.options.minRoleNeeded;
      } else {
        tab.options.minRoleNeeded = minRoleNeeded;
      }
      break;
    }

    case 'pages.tabs.toggleHidden': {
      const { pageId, tabId, hidden } = action;
      const page = draft.pages[pageId];
      if (!page) {
        console.error(
          `Cannot toggle hidden tab: Page with ID "${pageId}" not found`,
        );
        break;
      }
      const tab = data.db.tabs.byId[tabId];
      if (!tab) {
        console.error(
          `Cannot toggle hidden tab: Tab with ID "${tabId}" not found`,
        );
        break;
      }
      if (!tab.options) {
        tab.options = {};
      }
      if (hidden) {
        tab.options.hidden = true;
      } else {
        delete tab.options.hidden;
      }
      break;
    }

    case 'pagesOrPatterns.updateAliases': {
      const { id, idType, aliases } = action;
      const pageOrPattern =
        idType === 'page' ? draft.pages[id] : data.patternsState.patterns[id];
      pageOrPattern.aliases = aliases;
      break;
    }

    case 'pagesOrPatterns.settings.tableOfContents.update': {
      const { id, idType, tableOfContents } = action;
      const pageOrPattern =
        idType === 'page' ? draft.pages[id] : data.patternsState.patterns[id];
      if (!pageOrPattern.settings) {
        pageOrPattern.settings = {};
      }
      if (!pageOrPattern.settings.tableOfContents) {
        pageOrPattern.settings.tableOfContents = {};
      }
      mergeDeepInImmer({
        target: pageOrPattern.settings.tableOfContents,
        source: tableOfContents,
        sourceIsPartial: true,
      });
      break;
    }

    case 'pagesOrPatterns.settings.pageHeader.update': {
      const { id, idType, pageHeader } = action;
      const pageOrPattern =
        idType === 'page' ? draft.pages[id] : data.patternsState.patterns[id];
      if (!pageOrPattern.settings) {
        pageOrPattern.settings = {};
      }
      if (!pageOrPattern.settings.pageHeader) {
        pageOrPattern.settings.pageHeader = {};
      }
      mergeDeepInImmer({
        target: pageOrPattern.settings.pageHeader,
        source: pageHeader,
        sourceIsPartial: true,
      });
      break;
    }

    case 'pagesOrPatterns.settings.pageHeader.reset': {
      const { id, idType } = action;
      const pageOrPattern =
        idType === 'page' ? draft.pages[id] : data.patternsState.patterns[id];
      if (!pageOrPattern.settings) {
        pageOrPattern.settings = {};
      }
      delete pageOrPattern.settings.pageHeader;
      if (Object.keys(pageOrPattern.settings).length === 0) {
        delete pageOrPattern.settings;
      }
      break;
    }

    case 'pagesOrPatterns.tabs.setPrivacy': {
      const { id, idType, minRoleNeeded, tabId } = action;
      if (idType === 'page') {
        throw new Error('Tabs are only available on patterns');
      }
      const pageOrPattern = data.patternsState.patterns[id];
      const tab = pageOrPattern.tabs.find((t) => t.id === tabId);
      if (!minRoleNeeded) {
        delete tab.minRoleNeeded;
      } else {
        tab.minRoleNeeded = minRoleNeeded;
      }
      break;
    }

    case 'pagesOrPatterns.tabs.toggleHidden': {
      const { id, idType, hidden, tabId } = action;
      if (idType === 'page') {
        throw new Error('Tabs are only available on patterns');
      }
      const pageOrPattern = data.patternsState.patterns[id];
      const tab = pageOrPattern.tabs.find((t) => t.id === tabId);

      if (hidden) {
        tab.hidden = true;
      } else {
        delete tab.hidden;
      }
      break;
    }

    case DELETE_PAGE:
      /**
       * If the page doesn't exist, return early.
       * This allows us to delete a nav item whose page has already been deleted.
       */
      if (!draft.pages[action.payload.id]) return;

      draft.pages[action.payload.id].blockIds.forEach((blockId) => {
        removeBlock({
          blockId,
          data,
        });
      });
      delete draft.pages[action.payload.id];
      return;

    default: {
      const _exhaustiveCheck: never = action;
    }
  }
}
